import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Layers, Building2, Tags, ClipboardList, Lock, ChevronDown, Copy, Save, Loader2, Pencil, Trash2 } from "lucide-react";
import { NoAccess } from "@/components/NoAccess";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full min-w-0 rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 bg-white";
const MODULE_LABEL = {
  requests: "Requests", roster: "Residents (menu)", roster_title: "Residents page title", documents: "AI Documents", rules: "Rules", contacts: "Trusted Pros",
  profile: "Community Profile", field_services: "Field Services", reservations: "Reservations", events: "Events", renewals: "Renewals",
  emergency: "Emergency Alerts", broadcasts: "Messages", surveys: "Surveys", signatures: "E-Sign", reports: "Reports", public_page: "Public Page",
  conversations: "Conversations", feedback: "Feedback", team: "Management team", portal_requests: "My Requests (portal)",
};
const ROLE_LABEL = { hoa_manager: "Community Manager", board_member: "Board Member", regional_manager: "Community Manager", company_admin: "Administrator" };

// Company console → Industries: the ONE place a pack's words live. Edit here and every community
// on the pack relabels; clone a pack to stand up a new industry without a deploy.
export default function Industries() {
  const { role } = useDash();
  const [packs, setPacks] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({});
  const [clone, setClone] = useState(null);

  const load = () => api.get("/industries").then((r) => setPacks(r.data)).catch(() => { setPacks([]); toast.error("Couldn't load industry packs"); });
  useEffect(() => { if (role === "super_admin") load(); }, [role]);

  const loadDetail = (id) => api.get(`/industries/${id}`).then((r) => setDetail((d) => ({ ...d, [id]: r.data }))).catch(() => {});
  const toggle = (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!detail[id]) loadDetail(id);
  };

  if (role !== "super_admin") return <NoAccess page="Industries" hint="Industry packs are managed by Ask Redline." />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="industries-page">
      <div>
        <PageTitle title={<><Layers className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Industries</>} about={<>One pack per vertical. The words live <b>here, once</b> — the console, the portal and the concierge read them for every community on the pack.
          Pick a pack when you create a community; switch it later under Settings → The Concierge.</>} />
      </div>

      {packs === null && <div className="text-sm text-muted-foreground">Loading…</div>}
      <div className="space-y-3">
        {(packs || []).map((p) => {
          const d = detail[p.id];
          const open = openId === p.id;
          return (
            <div key={p.id} className="rounded-2xl border border-sand-200 bg-white overflow-hidden" data-testid={`industry-pack-${p.id}`}>
              <div role="button" tabIndex={0} onClick={() => toggle(p.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(p.id); } }}
                className="w-full text-left p-4 sm:p-5 flex items-start gap-3 sm:gap-4 cursor-pointer hover:bg-sand-50" data-testid={`industry-pack-${p.id}-toggle`}>
                <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><Layers className="h-5 w-5 text-redline-500" strokeWidth={1.5} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-heading font-bold text-base sm:text-lg">{p.name}</h2>
                    {p.is_default && <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[11px] font-semibold text-ink/70">Today's wording</span>}
                    <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[11px] font-semibold text-ink/70 inline-flex items-center gap-1">{p.is_default ? <Lock className="h-3 w-3" /> : <Pencil className="h-3 w-3" />} {p.is_default ? "Read-only" : p.source === "db" ? "Edited here" : "Built in · editable"}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{p.tagline}</p>
                  <div className="mt-2 flex items-center gap-3 sm:gap-4 text-xs text-ink/70 flex-wrap">
                    <span className="inline-flex items-center gap-1" data-testid={`industry-pack-${p.id}-communities`}><Building2 className="h-3.5 w-3.5" /> {p.communities} communit{p.communities === 1 ? "y" : "ies"}</span>
                    <span className="inline-flex items-center gap-1"><Tags className="h-3.5 w-3.5" /> {p.terms} words</span>
                    <span className="inline-flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {p.intake} request categories</span>
                  </div>
                </div>
                <ChevronDown className={`h-5 w-5 text-ink/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </div>
              {open && (
                <div className="border-t border-sand-100 p-4 sm:p-5" data-testid={`industry-pack-${p.id}-detail`}>
                  {!d ? <div className="text-muted-foreground text-sm">Loading…</div> : (
                    <PackEditor pack={d} onSaved={(np) => { setDetail((x) => ({ ...x, [p.id]: { ...np, keys: d.keys, editable: d.editable } })); load(); }}
                      onClone={() => setClone({ source: p.id, id: "", name: "", tagline: "" })}
                      onDeleted={() => { setOpenId(null); load(); }} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!clone} onOpenChange={(o) => !o && setClone(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">New industry from {packs?.find((x) => x.id === clone?.source)?.name}</DialogTitle></DialogHeader>
          {clone && (
            <div className="space-y-3">
              <label className="block"><span className="text-xs font-semibold text-ink/70">Short id <span className="font-normal text-ink/50">— letters, no spaces (plumbing, dental)</span></span>
                <input data-testid="clone-id" className={inputCls + " mt-1"} value={clone.id} onChange={(e) => setClone({ ...clone, id: e.target.value })} placeholder="plumbing" /></label>
              <label className="block"><span className="text-xs font-semibold text-ink/70">Name</span>
                <input data-testid="clone-name" className={inputCls + " mt-1"} value={clone.name} onChange={(e) => setClone({ ...clone, name: e.target.value })} placeholder="Plumbing & HVAC" /></label>
              <label className="block"><span className="text-xs font-semibold text-ink/70">Tagline</span>
                <input data-testid="clone-tagline" className={inputCls + " mt-1"} value={clone.tagline} onChange={(e) => setClone({ ...clone, tagline: e.target.value })} placeholder="Service calls, estimates and maintenance plans." /></label>
              <p className="text-xs text-muted-foreground">Everything is copied — words, module names, roles, intake, briefing. Edit the words right after.</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setClone(null)} className="rounded-full border border-sand-200 px-4 py-2 text-sm font-semibold">Cancel</button>
                <button type="button" data-testid="clone-submit" onClick={async () => {
                  try {
                    const r = await api.post("/industries", clone);
                    toast.success(`${r.data.name} created — edit its words below`);
                    setClone(null); await load(); setDetail((x) => ({ ...x, [r.data.id]: null })); setOpenId(r.data.id); loadDetail(r.data.id);
                  } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the pack"); }
                }} className="rounded-full bg-ink text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5"><Copy className="h-4 w-4" /> Create pack</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PackEditor({ pack, onSaved, onClone, onDeleted }) {
  const editable = pack.editable !== false;
  const [form, setForm] = useState(() => ({
    name: pack.name || "", tagline: pack.tagline || "",
    vocab: JSON.parse(JSON.stringify(pack.vocab || {})), modules: { ...(pack.modules || {}) }, roles: { ...(pack.roles || {}) },
    persona: pack.persona || "", boundaries: (pack.boundaries || []).join("\n"),
  }));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("words");
  const upd = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setWord = (k, which, v) => upd({ vocab: { ...form.vocab, [k]: { ...(form.vocab[k] || {}), [which]: v } } });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/industries/${pack.id}`, { ...form, boundaries: form.boundaries.split("\n").map((s) => s.trim()).filter(Boolean) });
      toast.success(`${r.data.name} saved — ${r.data.communities} communit${r.data.communities === 1 ? "y" : "ies"} relabel on next load`);
      setDirty(false); onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the pack"); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the ${pack.name} pack? Only possible when no community uses it.`)) return;
    try { await api.delete(`/industries/${pack.id}`); toast.success("Pack removed"); onDeleted?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove the pack"); }
  };

  const tabs = [["words", "Words"], ["modules", "Menu names"], ["roles", "Roles"], ["brief", "Concierge briefing"], ["intake", "Request intake"]];
  return (
    <div className="space-y-4" data-testid={`pack-editor-${pack.id}`}>
      {!editable && <p className="text-xs rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-ink/70" data-testid="pack-readonly-note">This is today's HOA wording — it's read-only. Clone it to start a new industry with different words.</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block"><span className="text-xs font-semibold text-ink/70">Pack name</span>
          <input data-testid="pack-name" disabled={!editable} className={inputCls + " mt-1"} value={form.name} onChange={(e) => upd({ name: e.target.value })} /></label>
        <label className="block"><span className="text-xs font-semibold text-ink/70">Tagline</span>
          <input data-testid="pack-tagline" disabled={!editable} className={inputCls + " mt-1"} value={form.tagline} onChange={(e) => upd({ tagline: e.target.value })} /></label>
      </div>

      <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" data-testid={`pack-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-ink text-white" : "bg-sand-100 text-ink/70 hover:text-ink"}`}>{l}</button>
        ))}
      </div>

      {tab === "words" && (
        <div className="divide-y divide-sand-100" data-testid="pack-words">
          <p className="text-xs text-muted-foreground pb-2">Every place the console says one of these, it reads the pack. One = singular, Many = plural.</p>
          {(pack.keys || []).map((k) => (
            <div key={k.key} className="py-2 grid gap-1.5 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)] sm:items-center sm:gap-2" data-testid={`pack-word-${k.key}`}>
              <div className="min-w-0"><div className="text-sm font-medium">{k.label}</div><div className="text-[11px] text-ink/50">{k.hint}</div></div>
              <div className="grid grid-cols-2 gap-1.5 sm:contents">
                <label className="min-w-0"><span className="sm:hidden text-[10px] font-bold uppercase tracking-wide text-ink/45">One</span>
                  <input data-testid={`pack-word-one-${k.key}`} disabled={!editable} className={inputCls} value={form.vocab[k.key]?.one || ""} onChange={(e) => setWord(k.key, "one", e.target.value)} /></label>
                <label className="min-w-0"><span className="sm:hidden text-[10px] font-bold uppercase tracking-wide text-ink/45">Many</span>
                  <input data-testid={`pack-word-many-${k.key}`} disabled={!editable} className={inputCls} value={form.vocab[k.key]?.many || ""} onChange={(e) => setWord(k.key, "many", e.target.value)} /></label>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "modules" && (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="pack-modules">
          {Object.keys(MODULE_LABEL).map((k) => (
            <label key={k} className="block min-w-0"><span className="text-[11px] font-semibold text-ink/60">{MODULE_LABEL[k]}</span>
              <input data-testid={`pack-module-${k}`} disabled={!editable} className={inputCls + " mt-0.5"} value={form.modules[k] || ""} placeholder={MODULE_LABEL[k]} onChange={(e) => upd({ modules: { ...form.modules, [k]: e.target.value } })} /></label>
          ))}
        </div>
      )}

      {tab === "roles" && (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="pack-roles">
          {Object.keys(ROLE_LABEL).map((k) => (
            <label key={k} className="block min-w-0"><span className="text-[11px] font-semibold text-ink/60">{ROLE_LABEL[k]}</span>
              <input data-testid={`pack-role-${k}`} disabled={!editable} className={inputCls + " mt-0.5"} value={form.roles[k] || ""} placeholder={ROLE_LABEL[k]} onChange={(e) => upd({ roles: { ...form.roles, [k]: e.target.value } })} /></label>
          ))}
        </div>
      )}

      {tab === "brief" && (
        <div className="space-y-3" data-testid="pack-brief">
          <label className="block"><span className="text-xs font-semibold text-ink/70">How the concierge sees its job</span>
            <textarea data-testid="pack-persona" disabled={!editable} rows={4} className={inputCls + " mt-1"} value={form.persona} onChange={(e) => upd({ persona: e.target.value })} /></label>
          <label className="block"><span className="text-xs font-semibold text-ink/70">Boundaries <span className="font-normal text-ink/50">— one per line</span></span>
            <textarea data-testid="pack-boundaries" disabled={!editable} rows={5} className={inputCls + " mt-1"} value={form.boundaries} onChange={(e) => upd({ boundaries: e.target.value })} /></label>
        </div>
      )}

      {tab === "intake" && (
        <div data-testid="pack-intake">
          {(pack.intake?.categories || []).length ? (
            <ul className="space-y-1">{pack.intake.categories.map((c) => <li key={c.code} className="text-[13px]"><b>{c.label}</b>{c.urgent && <span className="ml-1.5 rounded-full bg-red-50 text-red-700 px-1.5 text-[10px] font-bold">URGENT</span>} <span className="text-ink/60">— {c.description}</span></li>)}</ul>
          ) : <p className="text-ink/60 text-[13px]">Uses the standard built-in categories.</p>}
          {pack.intake?.hide_builtins?.length > 0 && <p className="text-[12px] text-ink/50 mt-1">Hides built-ins: {pack.intake.hide_builtins.join(", ")}</p>}
          <p className="text-xs text-muted-foreground mt-2">Intake categories are edited per community under Settings → Requests.</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-sand-100">
        <button type="button" data-testid="pack-clone" onClick={onClone} className="rounded-full border border-sand-200 px-4 py-2 text-sm font-semibold hover:border-ink inline-flex items-center gap-1.5"><Copy className="h-4 w-4" /> Clone as new industry</button>
        {pack.source === "db" && pack.communities === 0 && (
          <button type="button" data-testid="pack-delete" onClick={remove} className="rounded-full border border-red-200 text-red-700 px-4 py-2 text-sm font-semibold hover:bg-red-50 inline-flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Remove</button>
        )}
        {editable && (
          <button type="button" data-testid="pack-save" onClick={save} disabled={!dirty || saving}
            className="ml-auto rounded-full bg-ink text-white px-5 py-2 text-sm font-semibold hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save pack
          </button>
        )}
      </div>
    </div>
  );
}
